import { startOfDay, subSeconds } from 'date-fns'
import * as Effects from 'redux-saga/effects'
import { put, take } from 'redux-saga/effects'
import { fetchSelectedColumns } from '../../actions/bookingCalendar/calendar/selectColumn'
import { databaseForEventsIsEmpty } from '../../actions/bookingCalendar/event/requestDataForEvents'
import { databaseForRoomsIsEmpty } from '../../actions/bookingCalendar/room/requestDataForRooms'
import { callFetch } from '../../actions/fetch'
import loadNotificationPage from '../../actions/notification/loadMore'
import {
  changesDetected,
  IChangesDetected,
  ISyncCompleted,
  requestFirstSync,
  requestSynchronization,
  synchronizationCompleted,
  synchronizationRejected,
} from '../../actions/synchronize/synchronize'
import { fireToastError } from '../../actions/toasts/fireToast'
import { localStorage } from '../../database'
import i18n from '../../i18n'
import lastSync from '../../models/Misc/changeInfo'
import { IStore } from '../../reducers'
import {
  CalendarActions,
  EventActions,
  RoomActions,
} from '../../reducers/bookingCalendar/actionNames'
import { SynchronizeActions } from '../../reducers/synchronize/actionNames'
import {
  getLastSyncDate,
  getLastSyncSeconds,
  isFirstTime,
} from '../../reducers/synchronize/selectors/lastSync'
import { NotificationActions, UserAccountActions } from '../../reducers/userAccount/actionNames'
import { ActionNames } from '../../utils/FetchActionsName'
import fetchActionsName from '../../utils/FetchActionsName/fetchActionsName'
import takeLatestWithSignOff from '../../utils/Saga/TakeLatestUntilSignoff'

function* sync() {
  try {
    const actionNames = fetchActionsName(ActionNames.Synchronize)
    const [{ service, calendarId }] = yield Effects.all([
      Effects.take(actionNames.output),
      Effects.put(callFetch(actionNames.input, actionNames.output, lastSync)),
    ])

    const startSyncTime = new Date()
    const minutesAgo: Date = yield Effects.select(getLastSyncDate)
    const firstTime = yield Effects.select(isFirstTime)
    const lastModifyDate = firstTime
      ? startOfDay(minutesAgo)
      : subSeconds(minutesAgo, 60) //Tolerance for server - client time difference

    let putActions = []
    putActions.push(put(loadNotificationPage(0, lastModifyDate)))
    putActions.push(put(fetchSelectedColumns()))
    putActions.push(put({
      type: UserAccountActions.RequestLicenseData,
      requestSubs: true
    }))

    let takeActions = []
    takeActions.push(take(NotificationActions.SyncAccepted))
    takeActions.push(take(CalendarActions.ReplaceSelectedColumns))
    takeActions.push(take(UserAccountActions.LicenseDataAccepted))
    const result: boolean = yield Effects.call(
      service,
      calendarId,
      lastModifyDate,
    )

    if (result) {
      putActions = [...putActions, put(changesDetected(lastModifyDate))]
      takeActions = [
        ...takeActions,
        Effects.race({
          done: Effects.take(RoomActions.DataForRoomsAccepted),
          fail: Effects.take(RoomActions.DataForRoomsRejected),
        }),
        Effects.race({
          done: Effects.take(EventActions.DataForEventsAccepted),
          fail: Effects.take(EventActions.DataForEventsRejected),
        }),
      ]
    }

    yield Effects.all(putActions)
    yield Effects.all(takeActions)
    yield Effects.put(synchronizationCompleted(startSyncTime))
  } catch (e) {
    yield Effects.put(synchronizationRejected())
  }
}

function* syncWrapper() {
  yield* sync()
}

function synchronize() {
  return function*() {
    yield* sync()
  }
}

export function continuouslySynchronize(delayInMs: number) {
  const action = requestSynchronization()
  return function*() {
    yield Effects.delay(2000)

    const fallback = yield Effects.select(
      (state: IStore) => state.events.request.events.isFallbackLoading,
    )

    if (fallback) {
      yield Effects.race([
        Effects.take(EventActions.ToggleFallbackLoading),
        Effects.delay(30000),
      ])
    }

    while (true) {
      const lastModify: number = yield Effects.select(getLastSyncSeconds)
      if (0 === lastModify) {
        yield Effects.all([
          Effects.put(requestFirstSync()),
          Effects.race({
            done: Effects.take(SynchronizeActions.FlushedDataForSync),
            fail: Effects.delay(5000),
          }),
        ])
      }

      yield Effects.put(action)
      yield Effects.all([
        Effects.delay(delayInMs),
        Effects.race({
          done: Effects.take(SynchronizeActions.SyncCompleted),
          fail: Effects.take(SynchronizeActions.SyncRejected),
        }),
      ])
    }
  }
}

function* invalidSync() {
  yield Effects.put(fireToastError(i18n.t('')))
}

function* callForShortChanges(action: IChangesDetected) {
  yield Effects.all([
    Effects.put(databaseForRoomsIsEmpty(action.lastModifyDate)),
    Effects.put(databaseForEventsIsEmpty(action.lastModifyDate, false, true)),
  ])
}

function syncCompleted(action: ISyncCompleted) {
  const { UserDriver: Driver, Names } = localStorage
  const { UserAccountStorage } = Names

  Driver.setItem(
    UserAccountStorage.LastSync,
    action.lastModifyDate.getTime().toString(),
  )
}

export default function*() {
  const { REACT_APP_SHORT_SYNC_INTERVAL: shortInterval } = process.env

  yield Effects.takeLatest(SynchronizeActions.SyncRejected, invalidSync)
  yield takeLatestWithSignOff(SynchronizeActions.StartSync, synchronize())
  yield Effects.takeLatest(SynchronizeActions.NewChanges, callForShortChanges)
  yield Effects.takeLatest(SynchronizeActions.SyncCompleted, syncCompleted)
  yield Effects.takeLatest(SynchronizeActions.SetSyncDate, syncCompleted)
  yield Effects.takeLatest(SynchronizeActions.RequestSync, syncWrapper)

  yield takeLatestWithSignOff(
    action =>
      action.type === CalendarActions.IsReady ||
      action.type === SynchronizeActions.RequestContinuousSync ||
      (action.type === EventActions.DataForEventsAccepted &&
        action.fullRange === true),
    continuouslySynchronize(Number(shortInterval) || 60 * 1000),
  )
}
